// Cinematic dark sections for /solutions/* — unified palette: primary-main (teal) + secondary-main (orange).
// No card-spam. Big type, gradient orbs, hairlines, marquees, split layouts.
import Link from "next/link";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowUpLeft,
  Check,
  ChevronDown,
  Sparkles,
  Quote,
  Star,
  Plus,
  type LucideIcon,
} from "lucide-react";
import { useState } from "react";

/* ---------- Page shell ---------- */
export function SolutionShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-background text-foreground overflow-x-clip selection:bg-primary-main selection:text-foreground">
      {children}
    </div>
  );
}

/* ---------- Background orbs ---------- */
export function Orbs({ className = "" }: { className?: string }) {
  return (
    <>
      <div className={`pointer-events-none absolute -z-0 ${className}`}>
        <motion.div
          className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-primary-main/25 blur-[140px]"
          animate={{ scale: [1, 1.15, 1], x: [0, 30, 0] }}
          transition={{ duration: 14, repeat: Infinity }}
        />
        <motion.div
          className="absolute -bottom-40 -left-40 w-[600px] h-[600px] rounded-full bg-secondary-main/20 blur-[140px]"
          animate={{ scale: [1.1, 1, 1.1], x: [0, -30, 0] }}
          transition={{ duration: 16, repeat: Infinity }}
        />
      </div>
    </>
  );
}

/* ---------- Subtle grid background ---------- */
export function GridBg() {
  return (
    <div className="absolute inset-0 opacity-[0.08] pointer-events-none text-foreground">
      <div
        className="absolute inset-0"
        style={{
          backgroundImage:
            "linear-gradient(currentColor 1px, transparent 1px), linear-gradient(90deg, currentColor 1px, transparent 1px)",
          backgroundSize: "60px 60px",
          maskImage: "radial-gradient(ellipse at center, black 30%, transparent 75%)",
        }}
      />
    </div>
  );
}

/* ---------- Section wrapper ---------- */
export function Section({
  children,
  className = "",
  id,
}: {
  children: React.ReactNode;
  className?: string;
  id?: string;
}) {
  return (
    <section id={id} className={`relative py-24 md:py-32 ${className}`}>
      <div className="container mx-auto px-6 max-w-7xl relative">{children}</div>
    </section>
  );
}

/* ---------- Eyebrow + heading ---------- */
export function Eyebrow({ children }: { children: React.ReactNode }) {
  return (
    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-foreground/5 border border-border backdrop-blur-sm text-[11px] font-bold text-foreground/75 mb-6">
      <span className="relative flex h-1.5 w-1.5">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-main opacity-75" />
        <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-primary-main" />
      </span>
      {children}
    </div>
  );
}

export function SectionHeader({
  eyebrow,
  title,
  desc,
  center = true,
  accent = "primary",
}: {
  eyebrow?: string;
  title: React.ReactNode;
  desc?: string;
  center?: boolean;
  accent?: "primary" | "secondary";
}) {
  return (
    <div className={`max-w-3xl mb-16 ${center ? "mx-auto text-center" : ""}`}>
      {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
      <h2 className="text-4xl md:text-6xl font-black leading-[1.05] tracking-tight">
        {typeof title === "string" ? <span className="brand-gradient-text">{title}</span> : title}
      </h2>
      {desc && (
        <p className="mt-5 text-base md:text-lg text-muted-foreground leading-relaxed">{desc}</p>
      )}
    </div>
  );
}

/* ---------- Giant stat row (no cards, just hairlines) ---------- */
export function GiantStats({ items }: { items: { value: string; label: string; sub?: string }[] }) {
  return (
    <section className="relative border-y border-border">
      <div className="container mx-auto max-w-7xl grid grid-cols-2 md:grid-cols-4 divide-x divide-border [direction:ltr]">
        {items.map((s, i) => (
          <motion.div
            key={s.label}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.05 }}
            className="px-6 py-12 md:py-16 text-center [direction:rtl]"
          >
            <div className="text-5xl md:text-6xl font-black brand-gradient-text leading-none">
              {s.value}
            </div>
            <div className="mt-3 text-sm font-bold text-foreground">{s.label}</div>
            {s.sub && <div className="mt-1 text-[11px] text-muted-foreground/80">{s.sub}</div>}
          </motion.div>
        ))}
      </div>
    </section>
  );
}

/* ---------- Line feature list (no cards) ---------- */
export function LineFeatures({
  items,
}: {
  items: { icon: LucideIcon; title: string; desc: string }[];
}) {
  return (
    <div className="divide-y divide-border border-y border-border">
      {items.map((f, i) => (
        <motion.div
          key={f.title}
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ delay: i * 0.04 }}
          className="group grid md:grid-cols-[80px_1fr_auto] items-center gap-6 py-7 hover:bg-foreground/[0.03] transition-colors px-2"
        >
          <div className="text-muted-foreground/60 text-2xl font-black tabular-nums">
            {String(i + 1).padStart(2, "0")}
          </div>
          <div className="flex items-start gap-4">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-primary-main/20 to-secondary-main/20 border border-border flex items-center justify-center text-primary-main shrink-0 group-hover:scale-110 transition-transform">
              <f.icon size={20} />
            </div>
            <div>
              <div className="text-lg font-black text-foreground mb-1">{f.title}</div>
              <div className="text-sm text-muted-foreground leading-relaxed max-w-xl">{f.desc}</div>
            </div>
          </div>
          <ArrowUpLeft
            size={18}
            className="hidden md:block text-muted-foreground/60 group-hover:text-primary-main group-hover:-translate-x-1 group-hover:-translate-y-1 transition-all"
          />
        </motion.div>
      ))}
    </div>
  );
}

/* ---------- Alternating split (text + slot) ---------- */
export function SplitFeature({
  eyebrow,
  title,
  desc,
  bullets,
  slot,
  reverse = false,
}: {
  eyebrow: string;
  title: React.ReactNode;
  desc: string;
  bullets?: string[];
  slot: React.ReactNode;
  reverse?: boolean;
}) {
  return (
    <div
      className={`grid lg:grid-cols-2 gap-12 lg:gap-20 items-center ${reverse ? "lg:[direction:ltr]" : ""}`}
    >
      <div className={reverse ? "[direction:rtl]" : ""}>
        <Eyebrow>{eyebrow}</Eyebrow>
        <h3 className="text-3xl md:text-5xl font-black leading-tight mb-5">{title}</h3>
        <p className="text-muted-foreground leading-relaxed mb-6">{desc}</p>
        {bullets && (
          <ul className="space-y-3">
            {bullets.map((b) => (
              <li key={b} className="flex items-start gap-3 text-foreground/90">
                <span className="mt-1 w-5 h-5 rounded-md bg-primary-main/20 border border-primary-main/40 grid place-items-center shrink-0">
                  <Check size={11} className="text-primary-main" />
                </span>
                <span className="text-sm">{b}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className={reverse ? "[direction:rtl]" : ""}>{slot}</div>
    </div>
  );
}

/* ---------- Steps timeline (connected, not boxes) ---------- */
export function StepsTimeline({ steps }: { steps: { title: string; desc: string }[] }) {
  return (
    <div className="relative">
      <div className="hidden md:block absolute top-7 right-0 left-0 h-px bg-gradient-to-l from-transparent via-white/15 to-transparent" />
      <div className="grid md:grid-cols-4 gap-10 md:gap-6 relative">
        {steps.map((s, i) => (
          <motion.div
            key={s.title}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.08 }}
            className="text-right"
          >
            <div className="relative w-14 h-14 mb-5 mx-auto md:mx-0">
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-primary-main to-secondary-main blur-md opacity-50" />
              <div className="relative w-14 h-14 rounded-2xl bg-background border border-border grid place-items-center text-foreground font-black">
                {String(i + 1).padStart(2, "0")}
              </div>
            </div>
            <div className="text-lg font-black text-foreground mb-2">{s.title}</div>
            <div className="text-sm text-muted-foreground leading-relaxed">{s.desc}</div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Marquee logo strip (channels) ---------- */
export function Marquee({ items }: { items: { name: string; icon: LucideIcon }[] }) {
  const doubled = [...items, ...items];
  return (
    <div className="relative overflow-hidden border-y border-border bg-foreground/[0.03] py-6 [mask-image:linear-gradient(90deg,transparent,black_15%,black_85%,transparent)]">
      <motion.div
        className="flex gap-8 whitespace-nowrap"
        animate={{ x: ["0%", "-50%"] }}
        transition={{ duration: 28, repeat: Infinity, ease: "linear" }}
      >
        {doubled.map((c, i) => (
          <div key={i} className="flex items-center gap-3 px-6 py-2 text-foreground/75">
            <c.icon size={20} className="text-primary-main" />
            <span className="font-bold text-sm">{c.name}</span>
          </div>
        ))}
      </motion.div>
    </div>
  );
}

/* ---------- Compare split (no rows table — two stacked columns) ---------- */
export function CompareSplit({
  ours,
  theirs,
  oursLabel = "ترندی",
  theirsLabel = "روش سنتی",
}: {
  ours: string[];
  theirs: string[];
  oursLabel?: string;
  theirsLabel?: string;
}) {
  return (
    <div className="grid md:grid-cols-2 gap-5">
      <div className="relative rounded-3xl p-8 overflow-hidden border border-primary-main/30 bg-gradient-to-br from-primary-main/15 to-transparent">
        <div className="absolute -top-20 -left-20 w-60 h-60 rounded-full bg-primary-main/30 blur-3xl" />
        <div className="relative">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-main text-foreground text-[11px] font-black mb-5">
            <Check size={12} /> {oursLabel}
          </div>
          <ul className="space-y-3">
            {ours.map((o) => (
              <li key={o} className="flex items-start gap-3 text-foreground">
                <Check size={16} className="text-primary-main mt-0.5 shrink-0" />
                <span className="text-sm leading-relaxed">{o}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <div className="relative rounded-3xl p-8 overflow-hidden border border-border bg-foreground/[0.03]">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-foreground/10 text-foreground/75 text-[11px] font-black mb-5">
          {theirsLabel}
        </div>
        <ul className="space-y-3">
          {theirs.map((o) => (
            <li key={o} className="flex items-start gap-3 text-muted-foreground">
              <Plus size={16} className="rotate-45 text-muted-foreground/80 mt-0.5 shrink-0" />
              <span className="text-sm leading-relaxed line-through decoration-foreground/20">
                {o}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/* ---------- Big single testimonial / quote ---------- */
export function BigQuote({
  quote,
  name,
  role,
  rating = 5,
}: {
  quote: string;
  name: string;
  role: string;
  rating?: number;
}) {
  return (
    <div className="relative max-w-4xl mx-auto text-center">
      <Quote className="text-primary-main/30 mx-auto mb-6" size={56} />
      <p className="text-2xl md:text-4xl font-black leading-[1.4] text-foreground mb-8">
        «{quote}»
      </p>
      <div className="inline-flex items-center gap-4">
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary-main to-secondary-main shadow-lg" />
        <div className="text-right">
          <div className="font-black text-foreground">{name}</div>
          <div className="text-xs text-muted-foreground">{role}</div>
        </div>
        <div className="flex gap-0.5 mr-3 border-r border-border pr-3">
          {Array.from({ length: rating }).map((_, k) => (
            <Star key={k} size={14} className="text-secondary-main fill-current" />
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------- Mini testimonials row (compact, no heavy cards) ---------- */
export function MiniTestimonials({
  items,
}: {
  items: { name: string; role: string; quote: string }[];
}) {
  return (
    <div className="grid md:grid-cols-3 gap-px bg-foreground/10 rounded-3xl overflow-hidden border border-border">
      {items.map((t) => (
        <div key={t.name} className="bg-background p-7">
          <Quote size={20} className="text-primary-main mb-3" />
          <p className="text-sm text-foreground/90 leading-relaxed mb-5">{t.quote}</p>
          <div className="flex items-center gap-3 pt-4 border-t border-border">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-primary-main to-secondary-main" />
            <div>
              <div className="text-sm font-black text-foreground">{t.name}</div>
              <div className="text-[11px] text-muted-foreground/80 mt-0.5">{t.role}</div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ---------- FAQ ---------- */
export function FAQ({ items }: { items: { q: string; a: string }[] }) {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <div className="max-w-3xl mx-auto divide-y divide-border border-y border-border">
      {items.map((f, i) => (
        <div key={f.q}>
          <button
            onClick={() => setOpen(open === i ? null : i)}
            className="w-full flex items-center justify-between gap-4 py-6 text-right group"
          >
            <span className="font-bold text-foreground text-base md:text-lg group-hover:text-primary-main transition-colors">
              {f.q}
            </span>
            <span
              className={`shrink-0 w-9 h-9 rounded-full border border-border grid place-items-center transition-all ${open === i ? "bg-primary-main border-primary-main rotate-180" : "bg-foreground/[0.04]"}`}
            >
              <ChevronDown
                size={16}
                className={open === i ? "text-foreground" : "text-muted-foreground"}
              />
            </span>
          </button>
          {open === i && (
            <div className="pb-6 text-sm text-muted-foreground leading-relaxed">{f.a}</div>
          )}
        </div>
      ))}
    </div>
  );
}

/* ---------- Big CTA — full-width gradient ---------- */
export function FinalCTA({ title, desc }: { title: React.ReactNode; desc?: string }) {
  return (
    <section className="relative py-32 overflow-hidden">
      <Orbs className="inset-0" />
      <GridBg />
      <div className="container mx-auto px-6 max-w-4xl relative text-center">
        <Eyebrow>آماده شروع</Eyebrow>
        <h2 className="text-5xl md:text-7xl font-black leading-[1.05] tracking-tight mb-6">
          {title}
        </h2>
        {desc && (
          <p className="text-muted-foreground text-base md:text-lg max-w-2xl mx-auto mb-10">
            {desc}
          </p>
        )}
        <div className="flex flex-wrap items-center justify-center gap-4">
          <Link
            href="/auth"
            className="group relative inline-flex items-center gap-2 px-8 py-4 rounded-full bg-foreground text-background font-black shadow-2xl shadow-primary-main/20"
          >
            شروع رایگان ۱۴ روزه
            <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
          </Link>
          <Link
            href="/solutions"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-full border border-border text-foreground font-bold hover:bg-foreground/5 transition"
          >
            سایر راهکارها
          </Link>
        </div>
        <div className="mt-8 flex flex-wrap items-center justify-center gap-x-7 gap-y-2 text-xs text-muted-foreground/80">
          <span className="flex items-center gap-1.5">
            <Check size={13} className="text-primary-main" /> خدمات فراتر از تصور
          </span>
          <span className="flex items-center gap-1.5">
            <Check size={13} className="text-primary-main" /> راه‌اندازی ۵ دقیقه‌ای
          </span>
          <span className="flex items-center gap-1.5">
            <Check size={13} className="text-primary-main" /> پشتیبانی فارسی ۲۴/۷
          </span>
        </div>
      </div>
    </section>
  );
}

/* ---------- Hero Sparkle badge ---------- */
export function HeroBadge({ children }: { children: React.ReactNode }) {
  return (
    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-foreground/5 border border-border backdrop-blur-sm mb-7">
      <Sparkles size={12} className="text-secondary-main" />
      <span className="text-xs font-bold text-foreground/75">{children}</span>
    </div>
  );
}

/* ---------- Hero Title — gradient on selected words ---------- */
export function HeroTitle({
  pre,
  gradient,
  post,
}: {
  pre?: string;
  gradient: string;
  post?: string;
}) {
  return (
    <h1 className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-black leading-[1.05] tracking-tight mb-7">
      {pre && (
        <>
          {pre}
          <br />
        </>
      )}
      <span className="inline-block brand-gradient-text">{gradient}</span>
      {post && (
        <>
          <br />
          {post}
        </>
      )}
    </h1>
  );
}

/* ---------- Pricing teaser strip ---------- */
export function PricingStrip() {
  return (
    <section className="relative py-20 border-y border-border bg-foreground/[0.03]">
      <div className="container mx-auto px-6 max-w-6xl flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-right">
        <div>
          <Eyebrow>۱۴ روز رایگان</Eyebrow>
          <h3 className="text-2xl md:text-3xl font-black text-foreground">
            امروز شروع کن، اول ماه پرداخت
          </h3>
          <p className="text-muted-foreground text-sm mt-2">خدمات فراتر از تصور · لغو در هر زمان</p>
        </div>
        <div className="flex gap-3">
          <Link
            href="/pricing"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-main text-foreground font-black shadow-lg shadow-primary-main/30 hover:opacity-90"
          >
            مشاهده پلن‌ها <ArrowLeft size={15} />
          </Link>
          <Link
            href="/auth"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-border text-foreground font-bold hover:bg-foreground/5"
          >
            ثبت‌نام
          </Link>
        </div>
      </div>
    </section>
  );
}
